import { useState } from 'react'
import { DEFAULT_PANEL, SUCCESS_PANEL } from './DefaultPanels';
import { FormState } from './Aspects';
import axios from 'axios';
import { IAsset } from '../../models/Assets';
import { pictogramPaths } from './PictogramSelector';
import { useQuery } from '@apollo/client';
import { GET_PMV_STATUS } from '../../graphql/queries/pmv_status';
import { IPMVStatus } from '../../models/Pmv';

export interface IPMV {
    id: number;
    formState: FormState[];
}

const PMVHooks = () => {
    const [formStates, setFormStates] = useState<IPMV[]>([]);

    const hookPMVStatuses = useQuery(GET_PMV_STATUS, {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
        pollInterval: 1000 * 60, //Actualiza cada minuto
    });

    // Procesa los aspectos de un mensaje de PMV
    const processAspects = (stringHtml: string): FormState[] => {
        try {
            const aspects = stringHtml.split("[np]");
            let panel: FormState[] = [];

            aspects.forEach((aspect) => {
                if (aspect.trim() === "") return;

                // Extraer pictograma
                const pictogramMatch = aspect.match(/\[g(\d+)\]/);
                const pictogram = pictogramMatch ? pictogramPaths[pictogramMatch[1] as unknown as keyof typeof pictogramPaths] : "none";

                // Extraer mensajes
                const message1Match = aspect.match(/\[tr65,1,0,0\](.*?)(\[|$)/);
                const message2Match = aspect.match(/\[tr65,17,0,0\](.*?)(\[|$)/);
                const message3Match = aspect.match(/\[tr65,33,0,0\](.*?)(\[|$)/);

                const messages = [
                    message1Match?.[1].trim() || "",
                    message2Match?.[1].trim() || "",
                    message3Match?.[1].trim() || "",
                ];
                panel.push({
                    messages,
                    pictogram: pictogram,
                    textAlign: 'center',
                    textSize: 30,
                    textColor: '#ffffff',
                });
            });

            return panel.length > 0 ? panel : SUCCESS_PANEL;
        } catch (error) {
            return SUCCESS_PANEL;
        }

    };
    // Convierte un objeto aspects a un FormState[]
    const processAspectsObject = (aspects: { [key: string]: any }): FormState[] => {
        const panel: FormState[] = [];
        for (const key in aspects) {
            if (aspects.hasOwnProperty(key)) {
                const aspect = aspects[key];
                const { line1, line2, line3, textAlign, pictogram } = aspect;
                panel.push({
                    messages: [line1, line2, line3],
                    pictogram,
                    textSize: 30,
                    textColor: "#ffffff",
                    textAlign
                });
            }
        }
        return panel.length > 0 ? panel : SUCCESS_PANEL;
    };
    // Actualiza el estado de un PMV
    const updateFormStates = (panel: FormState[], pmvAsset: IAsset) => {
        setFormStates((prev) => {
            const existingIndex = prev.findIndex((item) => item.id === pmvAsset.id);
            if (existingIndex !== -1) {
                const updatedFormStates = [...prev];
                updatedFormStates[existingIndex] = { id: pmvAsset.id, formState: panel };
                return updatedFormStates;
            } else {
                return [...prev, { id: pmvAsset.id, formState: panel }];
            }
        });
    };
    // Obtiene el estado de un PMV, haciendo petición a la base de datos
    const getPMVStatus = (pmv: IAsset) => {
        const pmvStatus: IPMVStatus = hookPMVStatuses.data?.its_pmv_statuses.find((status: IPMVStatus) => status.pmv_id === pmv.id);
        if (pmvStatus) {
            const { is_online } = pmvStatus;
            try {
                if (is_online) {
                    const stringHtml = pmvStatus.message;
                    const panel = processAspects(stringHtml);
                    updateFormStates(panel, pmv);
                } else {
                    updateFormStates(DEFAULT_PANEL, pmv);
                }
            } catch (error) {
                updateFormStates(SUCCESS_PANEL, pmv);
            }
        } else {
            updateFormStates(DEFAULT_PANEL, pmv);
        }
    };
    // Obtiene el estado de un PMV, haciendo petición directamente al panel
    const fetchpmv = async (asset: IAsset) => {
        try {
            // Hacer petición al panel
            const pmv_response = await axios.get(asset.xaddr + '/testmsg.htm', {
                headers: {
                    "User-Agent": "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.36",
                },
            });
            if (pmv_response.status === 200) {
                const htmlContent = pmv_response.data;  // Extraer el mensaje del panel
                const parser = new DOMParser(); // Crear un parser para el HTML
                const doc = parser.parseFromString(htmlContent, "text/html");   // Parsear el HTML
                const textarea = doc.querySelector('textarea[name="Multistring"]'); // Buscar el textarea con el mensaje
                if (textarea) {
                    const stringHtml = (textarea as HTMLTextAreaElement).value;
                    const panel = processAspects(stringHtml);
                    updateFormStates(panel, asset);
                } else {
                    console.error("No se encontró el textarea con el nombre 'Multistring'");
                }
            } else {
                updateFormStates(DEFAULT_PANEL, asset);
            }
        } catch (error) {
            updateFormStates(DEFAULT_PANEL, asset);
        }
    };
    // Actualiza el estado de un PMV
    const updatePanelState = (pmvId: number, newMessage: string, is_online: boolean) => {
        if (!is_online) {
            setFormStates((prev) => {
                return prev.map((pmv) => {
                    if (pmv.id === pmvId) {
                        return { ...pmv, formState: DEFAULT_PANEL };
                    }
                    return pmv;
                });
            });
        } else {
            setFormStates((prev) => {
                return prev.map((pmv) => {
                    if (pmv.id === pmvId) {
                        const panel = processAspects(newMessage);
                        return { ...pmv, formState: panel };
                    }
                    return pmv;
                });
            });
        }
    };

    return {
        fetchpmv,
        formStates,
        hookPMVStatuses,
        getPMVStatus,
        updatePanelState,
        processAspects,
        processAspectsObject
    }
}

export default PMVHooks